import { OMSView } from '@src/components/OMS/OMSView';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import {
  AggressorFlagEnum,
  EMPTY_ARRAY,
  MixpanelEvent,
  MixpanelEventProperty,
  format,
  getOrderIncrements,
  useCurrenciesContext,
  useMixpanel,
  type Allocation,
  type MarketDataCardUnifiedLiquidityMarket,
  type OrderIntent,
  type Security,
  type SideEnum,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { orderSliceActions } from '../components/OMS/NewOrder/OrderSlice';
import type { PrimeOMSParams } from '../components/OMS/NewOrder/types';
import { openView } from '../components/OMS/OMSSlice';
import { getNormalizedOrderQty, useGetOrderCurrency } from '../components/OMS/useGetOrderCurrency';
import { useOrderPresets } from '../providers/OrderPresetsContext';
import { useDefaultSubAccount } from './useDefaultSubAccount';

interface UsePrimeFromOrderBookProps {
  security: Security | undefined;
  marketAccounts?: string[];
  unifiedLiquidityMarketSymbols?: MarketDataCardUnifiedLiquidityMarket[];
  subAccount?: string;
  orderPresetID?: string;
  aggregation?: string;
}

export function usePrimeFromOrderBook({
  security,
  marketAccounts,
  unifiedLiquidityMarketSymbols,
  subAccount,
  orderPresetID,
  aggregation,
}: UsePrimeFromOrderBookProps) {
  const mixpanel = useMixpanel();
  const { currenciesBySymbol } = useCurrenciesContext();
  const dispatch = useAppStateDispatch();
  const { defaultSubAccount } = useDefaultSubAccount();
  const { getOrderCurrency } = useGetOrderCurrency();
  const { orderPresetsByID } = useOrderPresets();

  return useCallback(
    ({
      side,
      price,
      quantity,
      intent = AggressorFlagEnum.Active,
    }: {
      side: SideEnum;
      price: string;
      quantity?: string;
      intent?: OrderIntent;
    }) => {
      if (security == null) {
        return;
      }

      mixpanel.track(MixpanelEvent.PrimeOrder, { [MixpanelEventProperty.Intent]: intent });
      const orderCurrency = getOrderCurrency(security, side);
      const currency = currenciesBySymbol.get(orderCurrency);
      const orderIncrements = getOrderIncrements({ Currency: orderCurrency }, security, currency);
      const normalizedQty = getNormalizedOrderQty({
        security,
        orderCurrency,
        size: quantity,
      });

      // TODO: Check if this really should be rounded or perhaps truncated instead or left unformatted
      const orderQty = format(normalizedQty, {
        spec: orderIncrements.quantityIncrement,
        truncate: true,
        pretty: false,
      });

      const orderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;

      // Create allocations based on the subAccount, orderPreset, or defaultSubAccount
      const allocations = subAccount
        ? [{ subAccount: subAccount, value: '1' } satisfies Allocation]
        : (orderPreset?.allocations ??
          (defaultSubAccount?.Name ? [{ subAccount: defaultSubAccount.Name, value: '1' } satisfies Allocation] : []));

      const order: PrimeOMSParams = {
        symbol: security?.Symbol,
        price,
        orderQty,
        side,
        marketAccounts: marketAccounts ?? EMPTY_ARRAY,
        unifiedLiquidityMarketSymbols,
        // Subaccount if orderPreset is not set
        subAccount: subAccount ?? defaultSubAccount?.Name,
        // Overriding the allocations here matches the behavior of the MarketCard, but we should probably refactor this
        orderPreset: orderPreset ? { ...orderPreset, allocations } : undefined,
        aggregation,
      };

      dispatch(openView(OMSView.NewOrderForm));
      dispatch(orderSliceActions.primeNewOrderForm(order));
    },
    [
      security,
      mixpanel,
      currenciesBySymbol,
      getOrderCurrency,
      dispatch,
      marketAccounts,
      unifiedLiquidityMarketSymbols,
      subAccount,
      defaultSubAccount,
      orderPresetID,
      orderPresetsByID,
      aggregation,
    ]
  );
}
